/**
 * The check report (POST /api/v1/drafts/:id/check; ADR 0021): validity, errors and warnings, the rendered
 * thumbnail, the package size and the headless smoke test's calls and console errors. Opened by the Check button,
 * and before an export when the check finds problems (with "Export anyway").
 */
import { CircleCheck, CircleX, TriangleAlert } from 'lucide-react';
import type { CheckReport } from '../api.ts';
import { formatBytes, t } from '../i18n.ts';
import { Modal } from '../panels/Dialogs.tsx';

export function CheckDialog({ report, onClose, onExportAnyway }: { report: CheckReport; onClose: () => void; onExportAnyway?: () => void }) {
  return (
    <Modal title={t('check.title')} onClose={onClose} testId="check-dialog" labelledBy="check-title">
      <div className={`check-summary ${report.valid ? 'ok' : 'bad'}`} data-testid="check-status" data-valid={report.valid ? 'true' : 'false'}>
        {report.valid ? <CircleCheck size={18} aria-hidden /> : <CircleX size={18} aria-hidden />}
        <strong>{report.valid ? t('check.valid') : t('check.invalid', { n: report.errors.length })}</strong>
        <span className="muted">{t('check.took', { ms: report.durationMs })}</span>
      </div>
      <div className="check-grid">
        <div>
          {report.errors.length > 0 && (
            <>
              <h3>{t('check.errors')}</h3>
              <ul className="check-issues" data-testid="check-errors">
                {report.errors.map((e, i) => (
                  <li key={i} className="err" data-code={e.code}>
                    <code>{e.code}</code> {e.message}
                    {e.details && <pre>{JSON.stringify(e.details, null, 1).slice(0, 1200)}</pre>}
                  </li>
                ))}
              </ul>
            </>
          )}
          {report.warnings.length > 0 && (
            <>
              <h3>{t('check.warnings')}</h3>
              <ul className="check-issues" data-testid="check-warnings">
                {report.warnings.map((w, i) => (
                  <li key={i} className="warn" data-code={w.code}>
                    <TriangleAlert size={13} aria-hidden /> <code>{w.code}</code> {w.message}
                  </li>
                ))}
              </ul>
            </>
          )}
          <dl className="check-facts">
            <dt>{t('check.size')}</dt>
            <dd data-testid="check-size">{report.sizeBytes === null ? '—' : t('check.sizeOf', { size: formatBytes(report.sizeBytes), limit: formatBytes(report.sizeLimitBytes) })}</dd>
            {report.sceneHash && (
              <>
                <dt>{t('check.sceneHash')}</dt>
                <dd className="mono">{report.sceneHash.slice(0, 23)}…</dd>
              </>
            )}
          </dl>
        </div>
        {report.thumbnail && (
          <figure className="check-thumb">
            <img src={report.thumbnail.dataUrl} width={report.thumbnail.width} height={report.thumbnail.height} alt={t('check.thumbnailAlt')} data-testid="check-thumbnail" />
            <figcaption>{t('check.thumbnail', { w: report.thumbnail.width, h: report.thumbnail.height, ms: Math.round(report.thumbnail.timestamp) })}</figcaption>
          </figure>
        )}
      </div>
      {report.smoke && (
        <details className="check-smoke" open={!report.smoke.ok}>
          <summary>{t('check.smoke', { n: report.smoke.calls.length, result: report.smoke.ok ? t('check.passed') : t('check.failed') })}</summary>
          {report.smoke.consoleErrors.length > 0 && (
            <ul className="check-issues" data-testid="check-console">
              {report.smoke.consoleErrors.map((c, i) => (
                <li key={i} className="err">
                  {c}
                </li>
              ))}
            </ul>
          )}
          <table className="check-calls">
            <thead>
              <tr>
                <th>{t('check.call')}</th>
                <th>{t('check.status')}</th>
                <th>{t('check.step')}</th>
                <th>ms</th>
              </tr>
            </thead>
            <tbody>
              {report.smoke.calls.map((c, i) => (
                <tr key={i} className={c.error || (c.statusCode ?? 200) >= 300 ? 'err' : ''}>
                  <td className="mono">{c.method}</td>
                  <td>{c.error ? c.error : `${c.statusCode ?? '—'}${c.statusMessage ? ` ${c.statusMessage}` : ''}`}</td>
                  <td>{c.currentStep ?? ''}</td>
                  <td>{c.durationMs?.toFixed(1) ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      <div className="modal-actions">
        {onExportAnyway && (
          <button type="button" className="btn" onClick={onExportAnyway} data-testid="check-export-anyway">
            {t('check.exportAnyway')}
          </button>
        )}
        <button type="button" className="btn primary" onClick={onClose} data-testid="check-close">
          {t('dialog.close')}
        </button>
      </div>
    </Modal>
  );
}
